/**
 * Venttra - Articles collection schema
 */
import { buildCollection } from "@firecms/core";
import { editorOrAdminPermissions } from "../permissions";
import { createArticlesLocalizationReviewView } from "../components/LocalizationReviewView";
import { CONTENT_LOCALES, LOCALE_LABELS } from "../constants";
import { collectArticleCompletenessIssues } from "../lib/completenessValidation";
import { CONTENT_STATUS_ENUM } from "../lib/contentStatus";
import {
  STATUS_FIELD_DESCRIPTION,
  publishedSnapshotProperties,
} from "../lib/publishedSnapshotProperties";
import { createStatusPreSave } from "../lib/statusPreSave";

const ARTICLE_BRAND_HINT =
  "V2 voice: Empathetic, Community-Driven & Conscious, Effortless. Keep language simple, human, and cooperative-first. Avoid VIP/Influencer terminology.";

export const articlesCollection = buildCollection({
  id: "articles",
  name: "Articles",
  singularName: "Article",
  path: "articles",
  icon: "Article",
  group: "Content",
  permissions: editorOrAdminPermissions,
  entityViews: [createArticlesLocalizationReviewView()],
  callbacks: {
    onPreSave: createStatusPreSave({
      collectCompleteness: collectArticleCompletenessIssues,
      entityLabel: "article",
    }),
  },
  properties: {
    title: {
      name: "Title",
      dataType: "map",
      description: `Localized title map. Keep titles clear, inviting, and non-corporate. ${ARTICLE_BRAND_HINT}`,
      properties: Object.fromEntries(
        CONTENT_LOCALES.map((locale) => [
          locale,
          { name: LOCALE_LABELS[locale], dataType: "string" as const },
        ])
      ),
    },
    slug: {
      name: "Slug",
      dataType: "string",
      description: "URL-safe slug",
      validation: { required: true },
    },
    content: {
      name: "Content",
      dataType: "map",
      description:
        `Localized Markdown content. Lead with traveler/community value, then sustainability as a natural extension. ${ARTICLE_BRAND_HINT}`,
      properties: Object.fromEntries(
        CONTENT_LOCALES.map((locale) => [
          locale,
          { name: LOCALE_LABELS[locale], dataType: "string" as const, markdown: true },
        ])
      ),
    },
    associated_guides: {
      name: "Associated Guides",
      dataType: "array",
      of: { dataType: "reference", path: "guides" },
      description: "Parent guides (many-to-many)",
    },
    associated_destinations: {
      name: "Associated Destinations",
      dataType: "array",
      of: { dataType: "reference", path: "destinations" },
      description: "Destinations linked to this article (many-to-many)",
    },
    status: {
      name: "Status",
      dataType: "string",
      enumValues: CONTENT_STATUS_ENUM,
      description: STATUS_FIELD_DESCRIPTION,
    },
    author_id: {
      name: "Author",
      dataType: "reference",
      path: "authors",
      description: "Author of the article",
    },
    ...publishedSnapshotProperties,
    created_at: {
      name: "Created At",
      dataType: "date",
      autoValue: "on_create",
    },
    updated_at: {
      name: "Updated At",
      dataType: "date",
      autoValue: "on_update",
    },
  },
});
